import { Modal } from '@components/Modal';
import { Button, Typography } from '@gurupay/gp-ui-library';
import { useLastUserActivityRef } from '@hooks/useLastUserActivityRef';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LogoutModalProps {
	expiresAt: string;
	logout: () => void;
	signOut: () => void;
	extendSession: () => void;
	timeOffset: number;
}

const FIVE_MINUTES = 5 * 60 * 1000;
const SHOW_MODAL_TIME = 60 * 1000 + 1000; // 1 minute + 1 second of buffer

export const LogoutModal: FC<LogoutModalProps> = ({
	expiresAt,
	logout,
	signOut,
	extendSession,
	timeOffset,
}) => {
	const endTime = new Date(expiresAt).getTime();
	const [isHidden, setIsHidden] = useState(true);
	const { t } = useTranslation('', { keyPrefix: 'idleModal' });
	const [secondsLeft, setSecondsLeft] = useState(0);
	const lastActivityRef = useLastUserActivityRef();

	const handleLogout = () => {
		setIsHidden(true);
		signOut();
	};

	const handleExtendSession = () => {
		setIsHidden(true);
		extendSession();
	};

	// Timer for modal countdown
	useEffect(() => {
		if (isHidden) {
			return;
		}
		const update = () => {
			const now = Date.now() - timeOffset;
			const diff = Math.floor((endTime - now) / 1000);
			setSecondsLeft(Math.max(0, diff));

			if (diff <= 0) {
				setIsHidden(true);
				logout();
			}
		};

		update();

		const interval = setInterval(update, 1000);
		return () => clearInterval(interval);
	}, [isHidden, endTime, timeOffset]);

	// Trigger modal display 60 seconds before the session expiry
	useEffect(() => {
		setIsHidden(true);

		const diff = endTime - SHOW_MODAL_TIME - (Date.now() - timeOffset);
		const triggerAction = () => {
			// Show modal only if user was inactive for more than 5 minutes
			if (Date.now() - lastActivityRef.current > FIVE_MINUTES) {
				setIsHidden(false);
			} else {
				// Extend session automatically if user is active (perform click scroll etc.)
				handleExtendSession();
			}
		};

		if (diff <= 0) {
			triggerAction();

			return;
		}

		const anim = (document.body?.animate([], { duration: diff }) as Animation) ?? null;
		if (anim) {
			anim.onfinish = triggerAction;
		}

		return () => anim?.cancel();
	}, [endTime, timeOffset]);

	return (
		<Modal isOpened={!isHidden}>
			<Typography level="h2">{t('title')}</Typography>
			<Typography level="text">{t('description', { seconds: secondsLeft })}</Typography>
			<div className="flex justify-end gap-space-16 mt-space-40">
				<Button className="flex-1/2" onClick={handleExtendSession} variant="primary">
					{t('stay')}
				</Button>
				<Button className="flex-1/2" onClick={handleLogout} variant="outlined">
					{t('out')}
				</Button>
			</div>
		</Modal>
	);
};
